import type { ReactNode } from 'react';

import { useReducedMotion } from 'framer-motion';

import { Box, Card, Typography, IconButton, CardContent } from '@mui/material';

import { Iconify } from '../iconify';
import { motion, pressActiveSelector } from '../../theme/motion';
import { themeConfig } from '../../theme/theme-config';
import { solidCard, solidCardHover, solidIconButton } from '../../utils/glass-styles';

// ----------------------------------------------------------------------

export type DashboardCardProps = {
  title: string;
  subtitle: string;
  icon: ReactNode;
  children?: ReactNode;
  onClick?: () => void;
  footer?: ReactNode;
  /** When true the card is non-interactive and shows a "Coming soon" badge
   *  in place of the "See all" affordance. */
  comingSoon?: boolean;
  'data-testid'?: string;
};

export function DashboardCard({
  title,
  subtitle,
  icon,
  children,
  onClick,
  footer,
  comingSoon = false,
  'data-testid': testId,
}: DashboardCardProps) {
  const interactive = onClick && !comingSoon;
  const reduceMotion = useReducedMotion() ?? false;
  return (
    <Card
      data-testid={testId}
      onClick={interactive ? onClick : undefined}
      sx={(theme) => ({
        height: '100%',
        borderRadius: 1.5,
        ...solidCard,
        ...(interactive && {
          cursor: 'pointer',
          // Scope the properties (no `transition: all`). Hover-lift is gated to
          // fine pointers so touch taps don't get a sticky hover state.
          transition: `background-color ${motion.duration.fast} ${motion.ease.out}, transform ${motion.duration.fast} ${motion.ease.out}, box-shadow ${motion.duration.fast} ${motion.ease.out}`,
          '@media (hover: hover) and (pointer: fine)': {
            '&:hover': {
              ...solidCardHover,
              transform: reduceMotion ? 'none' : 'translateY(-2px)',
              // Layered shadow so the lift reads as depth, not just a slide.
              boxShadow: theme.customShadows.z8,
            },
          },
          // Don't bounce the card when a button/toggle/row inside it is pressed.
          [pressActiveSelector]: { transform: reduceMotion ? 'none' : 'scale(0.98)' },
        }),
      })}
    >
      <CardContent
        sx={{
          px: 1.5,
          py: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              mr: 1.5,
              display: 'inline-flex',
              '& svg path:not([stroke="none"]), & svg line, & svg rect:not([stroke="none"])': {
                stroke: 'url(#dashboard-icon-gradient)',
              },
              '& svg path[fill], & svg rect[fill]': {
                fill: 'url(#dashboard-icon-gradient)',
              },
            }}
          >
            {icon}
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={(theme) => ({
                fontWeight: theme.typography.fontWeightSemiBold,
                lineHeight: 1.3,
                color: 'grey.50',
                textWrap: 'balance',
              })}
            >
              {title}
            </Typography>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              noWrap
              sx={(theme) => ({
                display: 'block',
                fontWeight: theme.typography.fontWeightRegular,
                fontSize: { md: theme.typography.pxToRem(12) },
                textWrap: 'pretty',
              })}
            >
              {subtitle}
            </Typography>
          </Box>
          {comingSoon && (
            <Typography
              variant="overline"
              sx={(theme) => ({
                fontSize: 10,
                letterSpacing: 0.5,
                fontWeight: theme.typography.fontWeightSemiBold,
                whiteSpace: 'nowrap',
                background: theme.gradients.primary.background,
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
              })}
            >
              Coming soon
            </Typography>
          )}
          {interactive && (
            <Box
              onClick={(e) => {
                e.stopPropagation();
                onClick();
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                color: 'grey.400',
                '&:hover': { color: 'grey.50' },
                transition: `color ${motion.duration.fast} ${motion.ease.out}`,
              }}
            >
              <Typography variant="caption" sx={(theme) => ({ fontWeight: theme.typography.fontWeightMedium })}>
                See all
              </Typography>
              <IconButton
                size="small"
                component="span"
                sx={{
                  ...solidIconButton,
                  borderRadius: '50%',
                  width: 24,
                  height: 24,
                  color: 'inherit',
                  pointerEvents: 'none',
                }}
              >
                <Iconify icon="solar:alt-arrow-right-outline" width={14} />
              </IconButton>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {children}
        </Box>
        {footer && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            {footer}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

// SVG gradient definitions required for dashboard card icons.
// Render once at the top of any page using DashboardCard.
export function DashboardGradientDefs() {
  return (
    <svg width="0" height="0" style={{ position: 'absolute' }}>
      <defs>
        <linearGradient id="dashboard-icon-gradient" gradientUnits="userSpaceOnUse" x1="0" y1="24" x2="24" y2="0">
          <stop offset="0%" stopColor={themeConfig.gradients.primary.colors[0]} />
          <stop offset="25%" stopColor={themeConfig.gradients.primary.colors[1]} />
          <stop offset="50%" stopColor={themeConfig.gradients.primary.colors[2]} />
          <stop offset="75%" stopColor={themeConfig.gradients.primary.colors[3]} />
          <stop offset="100%" stopColor={themeConfig.gradients.primary.colors[4]} />
        </linearGradient>
      </defs>
    </svg>
  );
}
